import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Send, CalendarDays, MapPin, Mail, Globe, Clock, Check, Video, ArrowLeft, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { LeadQualificationForm } from './LeadQualificationForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeSlots = [
  '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'
];

export const ContactSection = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('form');
  
  // Contact Form State
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [contactLoading, setContactLoading] = useState(false);

  // Booking State
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    notes: ''
  });
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState('');
  const [bookedSlots, setBookedSlots] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Fetch booked slots
  useEffect(() => {
    const fetchBookedSlots = async () => {
      try {
        const response = await axios.get(`${API}/appointments/booked-slots`);
        setBookedSlots(response.data);
      } catch (error) {
        console.error('Error fetching booked slots:', error);
      }
    };
    fetchBookedSlots();
  }, []);

  // Check if slot is booked
  const isSlotBooked = (date, time) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return bookedSlots.some(slot => slot.date === dateStr && slot.time === time);
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success(t('contact.form.success'));
      setContactForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } catch (error) {
      toast.error(t('contact.form.error'));
      console.error('Error submitting contact form:', error);
    } finally {
      setContactLoading(false);
    }
  };

  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Handle booking submission
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner une date et un créneau' : 'Please select a date and time slot');
      return;
    }

    setBookingLoading(true);

    try {
      const appointmentData = {
        ...bookingForm,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime
      };
      await axios.post(`${API}/appointments`, appointmentData);
      toast.success(t('contact.booking.success'));
      setBookingSuccess(true);
      // Refresh booked slots
      const response = await axios.get(`${API}/appointments/booked-slots`);
      setBookedSlots(response.data);
    } catch (error) {
      toast.error(t('contact.booking.error'));
      console.error('Error submitting booking:', error);
    } finally {
      setBookingLoading(false);
    }
  };

  const resetBooking = () => {
    setBookingSuccess(false);
    setBookingForm({ name: '', email: '', phone: '', company: '', service: '', notes: '' });
    setSelectedDate(undefined);
    setSelectedTime('');
  };

  const services = language === 'fr' 
    ? ['Pack Bronze', 'Pack Diamant', 'Pack Or', 'Cercles Stratégiques', 'Diagnostic 360°', 'Autre']
    : ['Bronze Pack', 'Diamond Pack', 'Gold Pack', 'Strategic Circles', '360° Diagnosis', 'Other'];

  return (
    <section id="contact" className="section-padding bg-white" data-testid="contact-section">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="font-serif text-4xl md:text-5xl font-bold text-[#004d40] mb-4"
            data-testid="contact-title"
          >
            {t('contact.title')}
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-[#004d40] rounded-2xl p-8 text-white h-full">
              <h3 className="font-serif text-2xl font-bold mb-6">
                BICENTER <span className="text-[#4caf50]">CONSULTING</span>
              </h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#4caf50]" />
                  </div>
                  <div>
                    <p className="font-medium">{t('contact.info.address')}</p>
                    <p className="text-white/70 text-sm">France</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#4caf50]" />
                  </div>
                  <div>
                    <p className="font-medium">{t('contact.info.email')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-[#4caf50]" />
                  </div>
                  <div>
                    <p className="font-medium">{t('contact.info.website')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#4caf50]" />
                  </div>
                  <div>
                    <p className="font-medium">{t('footer.consulting100')}</p>
                  </div>
                </div>
              </div>

              {/* Decorative */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <p className="text-white/60 text-sm italic">
                  "{t('hero.subtitle').substring(0, 100)}..."
                </p>
              </div>
            </div>
          </div>

          {/* Form/Booking Tabs */}
          <div className="lg:col-span-2">
            {/* Tab Buttons */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setActiveTab('form')}
                className={`flex-1 py-3 px-6 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeTab === 'form'
                    ? 'bg-[#004d40] text-white'
                    : 'bg-[#f8fafc] text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="tab-form"
              >
                <Send className="w-4 h-4 inline mr-2" />
                {t('contact.form.submit').replace('Envoyer', 'Message').replace('Send', 'Message')}
              </button>
              <button
                onClick={() => setActiveTab('booking')}
                className={`flex-1 py-3 px-6 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeTab === 'booking'
                    ? 'bg-[#004d40] text-white'
                    : 'bg-[#f8fafc] text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="tab-booking"
              >
                <CalendarDays className="w-4 h-4 inline mr-2" />
                {t('contact.booking.title')}
              </button>
              <button
                onClick={() => setActiveTab('qualify')}
                className={`flex-1 py-3 px-6 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeTab === 'qualify'
                    ? 'bg-[#004d40] text-white'
                    : 'bg-[#f8fafc] text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="tab-qualify"
              >
                <ClipboardList className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Qualifier mon projet' : 'Qualify my project'}
              </button>
            </div>

            {/* Contact Form */}
            {activeTab === 'form' && (
              <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <Input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="form-input"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <Input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="form-input"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <Input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="form-input"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.company')}
                    </label>
                    <Input
                      type="text"
                      value={contactForm.company}
                      onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                      className="form-input"
                      data-testid="contact-company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <Textarea
                    required
                    rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="form-input resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={contactLoading}
                  className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {contactLoading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t('contact.form.submit')}
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Booking Form */}
            {activeTab === 'booking' && !bookingSuccess && (
              <form onSubmit={handleBookingSubmit} className="space-y-6" data-testid="booking-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Calendar */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-4">
                      {t('contact.booking.selectDate')} *
                    </label>
                    <div className="bg-[#f8fafc] rounded-xl p-4">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                        className="rounded-md"
                        data-testid="booking-calendar"
                      />
                    </div>
                  </div>

                  {/* Time Slots & Service */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-4">
                        {t('contact.booking.selectTime')} *
                      </label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {timeSlots.map((time) => {
                          const booked = isSlotBooked(selectedDate, time);
                          return (
                            <button
                              key={time}
                              type="button"
                              disabled={booked}
                              onClick={() => setSelectedTime(time)}
                              className={`time-slot ${selectedTime === time ? 'selected' : ''} ${booked ? 'disabled' : ''}`}
                              data-testid={`time-slot-${time}`}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.booking.service')}
                      </label>
                      <Select
                        value={bookingForm.service}
                        onValueChange={(value) => setBookingForm({ ...bookingForm, service: value })}
                      >
                        <SelectTrigger className="w-full" data-testid="booking-service">
                          <SelectValue placeholder={language === 'fr' ? 'Sélectionnez un service' : 'Select a service'} />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <Input
                      type="text"
                      required
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                      className="form-input"
                      data-testid="booking-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <Input
                      type="email"
                      required
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                      className="form-input"
                      data-testid="booking-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <Input
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                      className="form-input"
                      data-testid="booking-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.company')}
                    </label>
                    <Input
                      type="text"
                      value={bookingForm.company}
                      onChange={(e) => setBookingForm({ ...bookingForm, company: e.target.value })}
                      className="form-input"
                      data-testid="booking-company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.booking.notes')}
                  </label>
                  <Textarea
                    rows={3}
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                    className="form-input resize-none"
                    data-testid="booking-notes"
                  />
                </div>

                {/* Selected Summary */}
                {selectedDate && selectedTime && (
                  <div className="bg-[#f8fafc] rounded-xl p-4 flex items-center gap-4" data-testid="booking-summary">
                    <div className="w-10 h-10 rounded-full bg-[#4caf50] flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-[#004d40]">
                        {selectedDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      <p className="text-slate-600 text-sm">{selectedTime} (Paris)</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 bg-[#5b5fc7]/5 rounded-xl p-4">
                  <Video className="w-5 h-5 text-[#5b5fc7] flex-shrink-0" />
                  <p className="text-sm text-slate-600">
                    {language === 'fr'
                      ? 'Le rendez-vous se déroulera en visioconférence via Microsoft Teams. Un lien de connexion vous sera envoyé par email.'
                      : 'The meeting will take place via Microsoft Teams video conference. A connection link will be sent to you by email.'}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={bookingLoading}
                  className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2"
                  data-testid="booking-submit"
                >
                  {bookingLoading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <CalendarDays className="w-5 h-5" />
                      {t('contact.booking.submit')}
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Booking Confirmation with Teams Link */}
            {activeTab === 'booking' && bookingSuccess && (
              <div className="text-center py-8" data-testid="booking-confirmation">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#4caf50]/10 flex items-center justify-center mb-6">
                  <Check className="w-10 h-10 text-[#4caf50]" />
                </div>
                <h3 className="text-2xl font-bold text-[#004d40] mb-3">
                  {language === 'fr' ? 'Rendez-vous confirmé !' : 'Appointment confirmed!'}
                </h3>
                <p className="text-slate-600 mb-8 max-w-md mx-auto">
                  {language === 'fr'
                    ? 'Un email de confirmation avec le lien Microsoft Teams vous a été envoyé. Retrouvez ci-dessous le lien pour rejoindre la réunion :'
                    : 'A confirmation email with the Microsoft Teams link has been sent to you. Find the meeting link below:'}
                </p>

                <div className="bg-[#f8fafc] rounded-2xl p-8 max-w-lg mx-auto border border-slate-100">
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M20.5 2H15C14.4477 2 14 2.44772 14 3V8C14 8.55228 14.4477 9 15 9H20.5C21.0523 9 21.5 8.55228 21.5 8V3C21.5 2.44772 21.0523 2 20.5 2Z" fill="#5b5fc7"/>
                      <path d="M12 5H3.5C2.67157 5 2 5.67157 2 6.5V17.5C2 18.3284 2.67157 19 3.5 19H12C12.8284 19 13.5 18.3284 13.5 17.5V6.5C13.5 5.67157 12.8284 5 12 5Z" fill="#5b5fc7"/>
                      <circle cx="18" cy="14" r="3.5" fill="#5b5fc7" opacity="0.7"/>
                    </svg>
                    <span className="font-bold text-[#5b5fc7] text-lg">Microsoft Teams</span>
                  </div>

                  <a
                    href="https://teams.microsoft.com/meet/365428879530866?p=YBu0lvl8C79OrdX4cH"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-[#5b5fc7] hover:bg-[#4a4eb5] text-white px-8 py-4 rounded-xl font-bold text-base transition-colors w-full justify-center"
                    data-testid="teams-join-link"
                  >
                    <Video className="w-5 h-5" />
                    {language === 'fr' ? 'Rejoindre la réunion Teams' : 'Join Teams Meeting'}
                  </a>

                  <p className="text-slate-400 text-xs mt-4 break-all">
                    https://teams.microsoft.com/meet/365428879530866?p=YBu0lvl8C79OrdX4cH
                  </p>
                </div>

                <button
                  onClick={resetBooking}
                  className="mt-8 inline-flex items-center gap-2 text-[#004d40] hover:text-[#00695c] font-medium text-sm transition-colors"
                  data-testid="booking-new"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {language === 'fr' ? 'Réserver un autre créneau' : 'Book another slot'}
                </button>
              </div>
            )}

            {/* Lead Qualification Form */}
            {activeTab === 'qualify' && (
              <LeadQualificationForm />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
